import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Biometrico.css';
import API from '../../utils/api';
import ConfirmationModal from './ConfirmationModal';
import DeviceStatus from './DeviceStatus';
import AttendanceReport from './AttendanceReport';
import { useToast } from '../../contexts/ToastContext';

const Biometrico = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Estados para la gestión del dispositivo
  const [deviceStatus, setDeviceStatus] = useState('disconnected');
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalBiometricUsers, setTotalBiometricUsers] = useState(0);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Estados para la gestión de usuarios y huellas
  const [biometricUsers, setBiometricUsers] = useState([]);
  const [databaseUsers, setDatabaseUsers] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => { });
  const [confirmMessage, setConfirmMessage] = useState('');

  // Estados para las checadas
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [startDate, setStartDate] = useState(getStartOfWeek());
  const [endDate, setEndDate] = useState(getEndOfWeek());
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);

  // Estados para configuración de dispositivo
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  const [portNumber, setPortNumber] = useState('');

  // Estados para la visualización
  const [viewMode, setViewMode] = useState('biometric');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // Funciones para calcular inicio y fin de semana
  function getStartOfWeek(date = new Date()) {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Ajuste para que la semana empiece en lunes
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  }

  function getEndOfWeek(date = new Date()) {
    const endOfWeek = new Date(getStartOfWeek(date));
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return endOfWeek;
  }

  // Memoizar los parámetros de conexión para evitar recálculos innecesarios
  const connectionParams = useMemo(() => {
    return {
      ip: ipAddress || localStorage.getItem('biometrico_ip') || '192.168.1.201',
      port: parseInt(portNumber || localStorage.getItem('biometrico_port') || '4370')
    };
  }, [ipAddress, portNumber]);

  // Cargar configuración inicial
  useEffect(() => {
    try {
      if (API.biometrico && typeof API.biometrico.getConnectionParams === 'function') {
        const params = API.biometrico.getConnectionParams();
        if (params) {
          setIpAddress(params.ip || '192.168.1.201');
          setPortNumber(params.port || '4370');
        }
      } else {
        // Fallback si no está disponible la función
        setIpAddress(localStorage.getItem('biometrico_ip') || '192.168.1.201');
        setPortNumber(localStorage.getItem('biometrico_port') || '4370');
      }
    } catch (error) {
      console.error('Error al cargar configuración de conexión:', error);
      toast.error('Error al cargar configuración de conexión');
    }
  }, []);

  // Conectar al dispositivo biométrico 
  const connectToDevice = useCallback(async () => {
    if (deviceStatus === 'connecting' || deviceStatus === 'connected') return;

    try {
      setDeviceStatus('connecting');
      setErrorMessage('');
      setLoading(true);

      toast.info(`Conectando al dispositivo en ${connectionParams.ip}:${connectionParams.port}...`);

      // Llamar a la API para conectar con el dispositivo
      const response = await window.biometrico.connect(connectionParams);

      if (response && response.success) {
        setDeviceStatus('connected');
        setDeviceInfo({
          ...response.deviceInfo,
          ipAddress: connectionParams.ip,
          port: connectionParams.port
        });

        toast.success('Dispositivo conectado correctamente');

        // Una vez conectado, cargar el primer lote de usuarios
        loadBiometricUsers(1, 1);
      } else {
        setDeviceStatus('error');
        const errorMsg = response?.message || `Error al conectar con el dispositivo en ${connectionParams.ip}:${connectionParams.port}`;
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error al conectar con dispositivo biométrico:', error);
      setDeviceStatus('error');
      const errorMsg = 'Error de comunicación con el servidor';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [deviceStatus, connectionParams]);

  // Desconectar el dispositivo
  const disconnectDevice = useCallback(async () => {
    try {
      setLoading(true);
      const result = await window.biometrico.disconnectDevice();

      if (result && result.success) {
        toast.success('Dispositivo desconectado correctamente');
      } else {
        toast.warning('El dispositivo fue desconectado, pero podría haber errores');
      }

      setDeviceStatus('disconnected');
      setDeviceInfo(null);
      setErrorMessage('');

      // Limpiar datos al desconectar
      setBiometricUsers([]);
      setTotalBiometricUsers(0);
      setTotalPages(1);
      setCurrentPage(1);
      setExpandedUsers({});
    } catch (error) {
      console.error('Error al desconectar dispositivo:', error);
      setErrorMessage('Error al desconectar el dispositivo');
      toast.error('Error al desconectar el dispositivo');
    } finally {
      setLoading(false);
    }
  }, []);

  // Asegurar la desconexión al desmontar el componente
  useEffect(() => {
    return () => {
      if (deviceStatus === 'connected') {
        disconnectDevice().catch(err => {
          console.error('Error al desconectar durante cleanup:', err);
        });
      }
    };
  }, [deviceStatus, disconnectDevice]);

  // Conectar automáticamente al iniciar
  useEffect(() => {
    if (deviceStatus === 'disconnected') {
      const timer = setTimeout(() => {
        connectToDevice();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [connectToDevice, deviceStatus]);

  // Cargar usuarios de la base de datos al iniciar
  useEffect(() => {
    loadDatabaseUsers();
  }, []);

  // Alternar la expansión de un usuario
  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => {
      const isExpanded = !prev[userId];

      // Si estamos expandiendo el usuario y no tiene sus huellas cargadas, hacerlo
      if (isExpanded) {
        const user = biometricUsers.find(u => u.userId === userId);
        if (user && (!user.fingerprints || user.fingerprints.length === 0)) {
          loadUserFingerprints(userId);
        }
      }

      return {
        ...prev,
        [userId]: isExpanded
      };
    });
  };

  // Cargar usuarios del biométrico
  const loadBiometricUsers = async (pageNum = 1, cn = 0) => {
    if (deviceStatus !== 'connected' && cn == 0) {
      const errorMsg = 'El dispositivo no está conectado. No se pueden cargar los usuarios.';
      setErrorMessage(errorMsg);
      toast.warning(errorMsg);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      setIsRefreshing(true);

      // Mostrar mensaje de carga
      const loadingMsg = `Cargando lote ${pageNum} de usuarios del dispositivo biométrico...`;
      setErrorMessage(loadingMsg);
      toast.info(loadingMsg, 3000);

      // Obtener los usuarios de la página solicitada
      const response = await window.biometrico.getUsers({
        ...connectionParams,
        useExistingConnection: true,
        page: pageNum,
        pageSize: pageSize
      });

      if (!response || !response.success) {
        throw new Error(response?.message || "Error obteniendo usuarios");
      }

      // Actualizar el total de usuarios y páginas
      const totalUsers = response.count || 0;
      const totalPgs = response.totalPages || Math.ceil(totalUsers / pageSize);

      setTotalBiometricUsers(totalUsers);
      setTotalPages(totalPgs);
      setCurrentPage(pageNum);

      // Obtener los usuarios
      const users = response.data || response.users || [];

      // Verificar si los usuarios tienen propiedades básicas
      const processedUsers = users.map(user => ({
        userId: user.userId || '',
        name: user.name || `Usuario ${user.userId}`,
        privilege: user.privilege || 0,
        enabled: !!user.enabled,
        fingerprintCount: user.fingerprintCount || 0
      }));

      // Actualizar el estado con los usuarios procesados
      setBiometricUsers(processedUsers);

      // Mensaje de éxito
      const successMsg = `Mostrando lote ${pageNum} de ${totalPgs}. Total de usuarios: ${totalUsers}.`;
      setErrorMessage(successMsg);
      toast.success(successMsg, 3000);

      // Limpiar mensaje después de un tiempo
      setTimeout(() => {
        setErrorMessage('');
      }, 3000);

      return true;
    } catch (error) {
      console.error('Error al cargar usuarios del biométrico:', error);
      const errorMsg = 'Error al cargar usuarios: ' + (error.message || 'Error desconocido');
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      return false;
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Cargar huellas de un usuario específico
  const loadUserFingerprints = async (userId) => {
    try {
      // Obtener el usuario del estado
      const userIndex = biometricUsers.findIndex(user => user.userId === userId);
      if (userIndex === -1) return;

      const user = biometricUsers[userIndex];

      // Verificar si ya tenemos las huellas
      if (user.fingerprints && user.fingerprints.length > 0) return;

      // Indicar que estamos cargando las huellas
      const updatedUsers = [...biometricUsers];
      updatedUsers[userIndex] = {
        ...user,
        loadingFingerprints: true
      };
      setBiometricUsers(updatedUsers);

      // Mostrar toast de carga
      toast.info(`Cargando huellas del usuario ${user.name}...`, 2000);

      // Obtener las huellas para este usuario
      const fingerprints = await window.biometrico.getUserFingerprints(userId);

      // Contar cuántas huellas registradas tiene
      const registeredCount = fingerprints ? fingerprints.filter(f => f.isRegistered).length : 0;

      // Actualizar el usuario con sus huellas
      const finalUsers = [...biometricUsers];
      finalUsers[userIndex] = {
        ...user,
        fingerprints: fingerprints || [],
        fingerprintCount: registeredCount,
        loadingFingerprints: false,
        fingerprintError: !fingerprints || fingerprints.length === 0
      };
      setBiometricUsers(finalUsers);

      if (registeredCount > 0) {
        toast.success(`${registeredCount} huellas encontradas para ${user.name}`);
      } else {
        toast.warning(`No se encontraron huellas registradas para ${user.name}`);
      }
    } catch (error) {
      console.error(`Error al cargar huellas para usuario ${userId}:`, error);
      toast.error(`Error al cargar huellas: ${error.message || 'Error desconocido'}`);

      // Marcar el error en el usuario
      const userIndex = biometricUsers.findIndex(user => user.userId === userId);
      if (userIndex !== -1) {
        const updatedUsers = [...biometricUsers];
        updatedUsers[userIndex] = {
          ...updatedUsers[userIndex],
          fingerprintError: true,
          loadingFingerprints: false
        };
        setBiometricUsers(updatedUsers);
      }
    }
  };

  // Función auxiliar para obtener el nombre del dedo
  const getFingerprintName = (index) => {
    const fingerNames = [
      'Meñique Izquierdo',
      'Anular Izquierdo',
      'Medio Izquierdo',
      'Índice Izquierdo',
      'Pulgar Izquierdo',
      'Pulgar Derecho',
      'Índice Derecho',
      'Medio Derecho',
      'Anular Derecho',
      'Meñique Derecho',
    ];
    return fingerNames[index] || `Dedo ${index}`;
  };

  // Cargar usuarios de la base de datos
  const loadDatabaseUsers = async () => {
    try {
      setLoading(true);
      toast.info('Cargando usuarios de la base de datos...', 2000);

      // Llamar a la API para obtener usuarios de la base de datos
      const users = await API.users.getAll();
      setDatabaseUsers(users || []);

      toast.success(`${users.length} usuarios cargados de la base de datos`);
    } catch (error) {
      console.error('Error al cargar usuarios de la base de datos:', error);
      toast.error('Error al cargar usuarios de la base de datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar registros de asistencia (checadas)
  const loadAttendanceRecords = async () => {
    if (deviceStatus !== 'connected') {
      toast.warning('El dispositivo no está conectado. No se pueden cargar los registros de asistencia.');
      return;
    }


    try {
      setIsLoadingAttendance(true);
      toast.info('Cargando registros de asistencia...', 2000);

      // Formatear fechas para el comando
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      // Usar el comando 'get_attendance'
      const response = await window.biometrico.sendCommand({
        action: 'get_attendance',
        data: {
          ...connectionParams,
          useExistingConnection: true,
          startDate: startDateStr,
          endDate: endDateStr
        }
      });
      console.log("llegando aqui ", response);

      if (!response || !response.success) {
        throw new Error(response?.message || "Error obteniendo registros de asistencia");
      }

      // Procesar los registros
      const records = response.records || [];

      // Organizar registros por usuario y por día
      const organizedRecords = processAttendanceRecords(records);

      setAttendanceRecords(organizedRecords);
      toast.success(`${records.length} registros de asistencia cargados`);
    } catch (error) {
      console.error('Error al cargar registros de asistencia:', error);
      toast.error('Error al cargar registros de asistencia: ' + (error.message || 'Error desconocido'));

      // Si falla, crear datos de ejemplo para pruebas de UI
      setAttendanceRecords(generateSampleAttendanceData());
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  // Procesar registros para organizarlos por usuario y día
  const processAttendanceRecords = (records) => {
    // Crear mapa de usuarios y sus registros
    const userMap = {};

    // Recorrer todos los registros
    records.forEach(record => {
      const { userId, name, date, time } = record;

      // Crear entrada para el usuario si no existe
      if (!userMap[userId]) {
        userMap[userId] = {
          userId,
          name,
          days: {}
        };
      }

      // Extraer solo la fecha (sin hora)
      const dateOnly = date.split('T')[0];

      // Crear entrada para el día si no existe
      if (!userMap[userId].days[dateOnly]) {
        userMap[userId].days[dateOnly] = [];
      }

      // Agregar hora al día
      userMap[userId].days[dateOnly].push(time);
    });

    // Convertir mapa a array y procesar las horas para cada día
    const processedRecords = Object.values(userMap).map(user => {
      const processedDays = {};

      // Para cada día, encontrar la primera y última hora
      Object.keys(user.days).forEach(day => {
        const times = user.days[day].sort();
        processedDays[day] = {
          firstTime: times[0], // Hora de entrada (primera checada)
          lastTime: times[times.length - 1] == times[0] ? '' : times[times.length - 1], // Hora de salida (última checada)
          allTimes: times // Mantener todas las horas por si se necesitan
        };
      });

      return {
        userId: user.userId,
        name: user.name,
        days: processedDays
      };
    });

    return processedRecords;
  };

  // Generar datos de muestra para pruebas
  const generateSampleAttendanceData = () => {
    // Usar lista de usuarios existente o crear lista de ejemplo
    const userList = biometricUsers.length > 0 ?
      biometricUsers.slice(0, 10) :
      [
        { userId: '1', name: 'JENNIFER CITLALI RODRIGUEZ DURAN' },
        { userId: '2', name: 'MOISES RAUL HERNANDEZ MEDINA' },
        { userId: '3', name: 'JAIME PEREZ HERRERA' },
        { userId: '4', name: 'MORALES MARTINEZ CLAUDIA LUZ' },
        { userId: '5', name: 'JIMENEZ MIRELES DAFNE ISUI' },
        { userId: '6', name: 'RAMIREZ KARLA' },
        { userId: '7', name: 'SALAS GUZMAN OSCAR TAREEK' },
        { userId: '8', name: 'GONZALEZ FLORES MARIA FATIMA' },
        { userId: '9', name: 'PEREZ FLORES ANGEL ABNER' },
        { userId: '10', name: 'SORIA TISCAREÑO KEIRY AILYN' }
      ];

    // Generar registros para una semana
    return userList.map(user => {
      const days = {};

      // Por cada día de la semana
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        // No todos los usuarios tienen registros todos los días
        if (Math.random() > 0.3) { // 70% probabilidad de tener registros
          const date = new Date(startDate);
          date.setDate(date.getDate() + dayIndex);
          const dateStr = date.toISOString().split('T')[0];

          // Generar horarios aleatorios
          const entryHour = 8 + Math.floor(Math.random() * 4); // Entre 8 y 11
          const entryMinute = Math.floor(Math.random() * 60);

          const exitHour = 17 + Math.floor(Math.random() * 6); // Entre 17 y 22
          const exitMinute = Math.floor(Math.random() * 60);

          const formatTime = (hour, minute) => {
            return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`;
          };

          days[dateStr] = {
            firstTime: formatTime(entryHour, entryMinute),
            lastTime: formatTime(exitHour, exitMinute),
            allTimes: [
              formatTime(entryHour, entryMinute),
              formatTime(exitHour, exitMinute)
            ]
          };
        }
      }

      return {
        userId: user.userId,
        name: user.name,
        days
      };
    });
  };

  // Guardar configuración de conexión
  const saveConnectionConfig = () => {
    try {
      if (API.biometrico && typeof API.biometrico.setConnectionParams === 'function') {
        API.biometrico.setConnectionParams({
          ip: ipAddress,
          port: portNumber
        });
      } else {
        localStorage.setItem('biometrico_ip', ipAddress);
        localStorage.setItem('biometrico_port', portNumber);
      }

      setShowConfigModal(false);
      setErrorMessage('');
      toast.success('Configuración guardada correctamente');

      // Preguntar si desea reconectar con la nueva configuración
      if (deviceStatus === 'connected') {
        setConfirmMessage('¿Desea reconectar el dispositivo con la nueva configuración?');
        setConfirmAction(() => async () => {
          await disconnectDevice();
          setTimeout(() => connectToDevice(), 1000);
        });
        setShowConfirmModal(true);
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      toast.error('Error al guardar configuración');
    }
  };

  // Volver al dashboard
  const handleBackToDashboard = () => {
    // Asegurar desconexión antes de navegar
    if (deviceStatus === 'connected') {
      disconnectDevice().finally(() => {
        navigate('/dashboard');
      });
    } else {
      navigate('/dashboard');
    }
  };

  // Cambiar modo de visualización
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  // Cambiar pestaña activa
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'attendance' && attendanceRecords.length === 0) {
      loadAttendanceRecords();
    }
  };

  // Filtrar usuarios según el término de búsqueda
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return biometricUsers;

    const search = searchTerm.toLowerCase();
    return biometricUsers.filter(user => {
      const nameMatch = user.name && user.name.toLowerCase().includes(search);
      const idMatch = user.userId && user.userId.toString().toLowerCase().includes(search);
      return nameMatch || idMatch;
    });
  }, [biometricUsers, searchTerm]);

  // Componente para los botones de navegación entre lotes
  const BatchNavigationControls = () => {
    // No mostrar si no hay usuarios o hay solo una página
    if (totalBiometricUsers === 0 || totalPages <= 1) return null;

    // Función para ir a una página específica
    const goToPage = (page) => {
      if (page < 1 || page > totalPages || page === currentPage) return;
      loadBiometricUsers(page);
    };

    // Generar array de números de página a mostrar
    const getPageNumbers = () => {
      const delta = 1; // Mostrar 1 página a cada lado de la actual
      const pages = [];

      // Siempre incluir primera y última página
      pages.push(1);

      // Añadir páginas cercanas a la actual
      for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
        pages.push(i);
      }

      // Añadir última página si no es la primera
      if (totalPages > 1) {
        pages.push(totalPages);
      }

      // Añadir puntos suspensivos donde sea necesario
      const pagesWithDots = [];
      let prevPage = 0;

      for (const page of pages) {
        if (prevPage && page - prevPage > 1) {
          pagesWithDots.push('...');
        }
        pagesWithDots.push(page);
        prevPage = page;
      }

      return pagesWithDots;
    };

    return (
      <div className="batch-navigation">
        <div className="batch-info">
          <span>Mostrando lote {currentPage} de {totalPages}</span>
          <span className="total-count">Total: {totalBiometricUsers} usuarios</span>
        </div>

        <div className="batch-controls">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1 || loading}
            title="Primera página"
          >
            «
          </button>

          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            title="Página anterior"
          >
            ‹
          </button>

          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? goToPage(page) : null}
              className={currentPage === page ? 'active' : ''}
              disabled={typeof page !== 'number' || page === currentPage || loading}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            title="Página siguiente"
          >
            ›
          </button>

          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages || loading}
            title="Última página"
          >
            »
          </button>
        </div>

        <div className="batch-actions">
          <button
            onClick={() => loadBiometricUsers(currentPage)}
            disabled={loading}
            title="Refrescar lote actual"
            className="refresh-button"
          >
            ↻ Refrescar
          </button>

          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              setPageSize(newSize);
              // Resetear a primera página al cambiar tamaño
              setCurrentPage(1);
              loadBiometricUsers(1);
            }}
            disabled={loading}
          >
            <option value={50}>50 por lote</option>
            <option value={100}>100 por lote</option>
            <option value={200}>200 por lote</option>
          </select>
        </div>
      </div>
    );
  };

  // Renderizado de la lista de usuarios
  const renderUserList = () => {
    if (loading && biometricUsers.length === 0) {
      return (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <div className="no-users-message">
          {searchTerm ?
            <p>No se encontraron usuarios que coincidan con "{searchTerm}"</p> :
            <p>No hay usuarios registrados en el dispositivo biométrico o no se ha conectado al dispositivo.</p>
          }
        </div>
      );
    }

    return (
      <div className="user-list-container">
        <div className="user-list">
          {filteredUsers.map(user => (
            <div key={user.userId} className="user-card">
              <div className="user-header" onClick={() => toggleUserExpanded(user.userId)}>
                <div className="user-name-id">
                  <span className="user-name">{user.name}</span>
                  <span className="user-id">ID: {user.userId}</span>
                </div>
                <div className="user-fingerprint-count">
                  <div className="huella-indicator">
                    <span className="huella-count">{user.fingerprintCount || 0}</span>
                    <span>huellas</span>
                  </div>
                  <button className="expand-button">
                    {expandedUsers[user.userId] ? '▲' : '▼'}
                  </button>
                </div>
              </div>

              {/* Estado de carga de huellas */}
              {expandedUsers[user.userId] && user.loadingFingerprints && (
                <div className="fingerprint-loading">
                  <div className="spinner-small"></div>
                  <p>Cargando huellas...</p>
                </div>
              )}

              {/* Mostrar huellas si el usuario está expandido y tiene huellas cargadas */}
              {expandedUsers[user.userId] && user.fingerprints && !user.loadingFingerprints && (
                <div className="fingerprints-container">
                  <h4>Huellas disponibles</h4>
                  <div className="fingerprints-grid">
                    {user.fingerprints.map(fingerprint => (
                      <div
                        key={`${user.userId}_${fingerprint.fingerIndex}`}
                        className={`fingerprint-item ${fingerprint.isRegistered ? 'registered' : ''}`}
                      >
                        <div className="finger-icon">👆</div>
                        <span className="finger-name">{getFingerprintName(fingerprint.fingerIndex)}</span>
                        <span className="finger-status">{fingerprint.isRegistered ? 'Registrado' : 'No registrado'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mostrar error si no se pudieron cargar las huellas */}
              {expandedUsers[user.userId] && user.fingerprintError && !user.loadingFingerprints && (
                <div className="fingerprint-error">
                  <p>No se pudieron cargar las huellas para este usuario.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Renderizado del componente principal
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Gestión de Biométrico Checador</h1>
        <div className="user-info">
          <span>Usuario: {API.auth.getCurrentUser()?.usuario || 'Admin'}</span>
          <button
            className="back-button"
            onClick={handleBackToDashboard}
            disabled={loading}
          >
            Volver al Dashboard
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="biometrico-container">
          {/* Estado del dispositivo */}
          <DeviceStatus
            status={deviceStatus}
            deviceInfo={deviceInfo}
            onConnect={connectToDevice}
            onDisconnect={disconnectDevice}
            onConfigureDevice={() => setShowConfigModal(true)}
            loading={loading}
          />

          {/* Tabs de navegación principal */}
          <div className="main-tabs">
            <button
              className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => handleTabChange('users')}
            >
              Usuarios
            </button>
            <button
              className={`tab-button ${activeTab === 'attendance' ? 'active' : ''}`}
              onClick={() => handleTabChange('attendance')}
            >
              Checadas Semanales
            </button>
          </div>

          {/* Contenido según la pestaña activa */}
          {activeTab === 'users' ? (
            <>
              {/* Controles de visualización */}
              <div className="view-controls">
                <div className="view-tabs">
                  <button
                    className={`view-tab ${viewMode === 'biometric' ? 'active' : ''}`}
                    onClick={() => handleViewModeChange('biometric')}
                  >
                    Usuarios Biométrico
                  </button>
                  <button
                    className={`view-tab ${viewMode === 'database' ? 'active' : ''}`}
                    onClick={() => handleViewModeChange('database')}
                  >
                    Usuarios Base de Datos
                  </button>
                </div>

                <div className="refresh-controls">
                  <button
                    onClick={() => loadBiometricUsers(currentPage)}
                    className="refresh-button"
                    disabled={deviceStatus !== 'connected' || loading}
                  >
                    {isRefreshing ? 'Actualizando...' : '↻ Actualizar Biométrico'}
                  </button>

                  <button
                    className="refresh-button"
                    onClick={loadDatabaseUsers}
                    disabled={loading}
                  >
                    ↻ Actualizar BD
                  </button>
                </div>
              </div>

              {/* Vista principal de usuarios */}
              <div className="main-view">
                {/* Barra de búsqueda */}
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Buscar usuarios..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Navegación por lotes */}
                {deviceStatus === 'connected' && <BatchNavigationControls />}

                {/* Lista de usuarios */}
                {renderUserList()}
              </div>
            </>
          ) : (
            /* Pestaña de Checadas Semanales */
            <div className="attendance-view">
              <div className="attendance-controls">
                <div className="date-controls">
                  <div className="date-input-group">
                    <label>Desde:</label>
                    <input
                      type="date"
                      value={startDate.toISOString().split('T')[0]}
                      onChange={(e) => setStartDate(new Date(e.target.value))}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>Hasta:</label>
                    <input
                      type="date"
                      value={endDate.toISOString().split('T')[0]}
                      onChange={(e) => setEndDate(new Date(e.target.value))}
                    />
                  </div>
                  <button
                    className="load-button"
                    onClick={loadAttendanceRecords}
                    disabled={deviceStatus !== 'connected' || isLoadingAttendance}
                  >
                    {isLoadingAttendance ? 'Cargando...' : 'Cargar Registros'}
                  </button>
                </div>
                <div className="attendance-filters">
                  <input
                    type="text"
                    placeholder="Buscar empleado..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Tabla de checadas */}
              {isLoadingAttendance ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Cargando registros de asistencia...</p>
                </div>
              ) : (
                <AttendanceReport
                  attendanceRecords={attendanceRecords}
                  startDate={startDate}
                  endDate={endDate}
                  searchTerm={searchTerm}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de configuración del dispositivo */}
      {showConfigModal && (
        <div className="modal-overlay">
          <div className="config-modal">
            <div className="modal-header">
              <h3>Configurar Conexión del Biométrico</h3>
              <button className="close-btn" onClick={() => setShowConfigModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="deviceIp">Dirección IP:</label>
                <input
                  type="text"
                  id="deviceIp"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  placeholder="Ej: 192.168.1.201"
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="devicePort">Puerto:</label>
                <input
                  type="text"
                  id="devicePort"
                  value={portNumber}
                  onChange={(e) => setPortNumber(e.target.value)}
                  placeholder="Ej: 4370"
                  className="input-field"
                />
              </div>

              <div className="config-description">
                <p>Configure la dirección IP y puerto del dispositivo biométrico MB160.</p>
                <p>Generalmente el puerto es 4370 por defecto.</p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-primary"
                onClick={saveConnectionConfig}
              >
                Guardar Configuración
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowConfigModal(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          confirmAction();
          setShowConfirmModal(false);
        }}
        message={confirmMessage}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      <footer className="dashboard-footer">
        Sistema de Biométrico MB160 - Presto v2 © 2025
      </footer>
    </div>
  );
};

export default Biometrico;